// The Inbox's workspace rows when they come from elsewhere than Me's server
// (a Hub's desktop: from the cloud, with this PC's own sign-in).
//
// Promise:
// - Only with the host's workspace source and a cloud account to read from;
//   otherwise nothing is asked and the Inbox is Me's alone.
// - The rows arrive by one stream (the cloud sends them again only when they
//   change); nothing is asked at intervals. While the stream is down the rows
//   last seen stay, marked as out of reach, and the Inbox says so.
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InboxEntry } from "@trameria/core/notices-shared";
import { useEffect } from "react";
import { useApi, useMeApp } from "../app/context";
import { openEventStream } from "../realtime/event-stream";

export interface WorkspaceInbox {
  /** off: no source or no account; down: out of reach (the rows last seen). */
  state: "off" | "waiting" | "ok" | "down";
  items: InboxEntry[];
}

const OFF: WorkspaceInbox = { state: "off", items: [] };

export const workspaceInboxKey = ["me", "workspace", "inbox"] as const;
const tiedKey = ["me", "workspace", "tied"] as const;

/** The rows as the stream left them (never read by itself). */
export const workspaceInboxQuery = () =>
  queryOptions<WorkspaceInbox>({
    queryKey: workspaceInboxKey,
    queryFn: () => OFF,
    initialData: OFF,
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * What waits, Me's and the workspaces', in the Inbox's order. A row of theirs
 * that opens what one of mine opens is left out (Me's says it already).
 */
export function mergeInbox(
  mine: readonly InboxEntry[],
  theirs: readonly InboxEntry[],
): InboxEntry[] {
  // Me's come in the server's order; only the workspaces' are placed among them.
  if (theirs.length === 0) return [...mine];
  const opened = new Set(mine.map((e) => e.to));
  const rank = (e: InboxEntry) => (e.kind === "mention" ? 0 : 1);
  return [...mine, ...theirs.filter((e) => !opened.has(e.to))].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      b.at.localeCompare(a.at) ||
      a.key.localeCompare(b.key),
  );
}

export function useWorkspaceInbox(): WorkspaceInbox {
  return useQuery(workspaceInboxQuery()).data;
}

/** The host's workspace source when there is an account to read it with. */
export function useWorkspaceSource() {
  const source = useMeApp().extensions.workspace;
  const api = useApi();
  const tied = useQuery({
    queryKey: tiedKey,
    queryFn: () => api<{ tied: boolean }>(source?.state ?? ""),
    enabled: source !== undefined,
    staleTime: 60_000,
  }).data?.tied;
  return tied ? source : undefined;
}

/** Keeps the rows in step while the app runs (the Inbox part's background). */
export function WorkspaceInboxStream() {
  const qc = useQueryClient();
  const url = useWorkspaceSource()?.inbox;
  useEffect(() => {
    if (!url) {
      qc.setQueryData(workspaceInboxKey, OFF);
      return;
    }
    const set = (next: (before: WorkspaceInbox) => WorkspaceInbox) =>
      qc.setQueryData<WorkspaceInbox>(workspaceInboxKey, (before) =>
        next(before ?? OFF),
      );
    set((b) => ({ ...b, state: b.state === "ok" ? "ok" : "waiting" }));
    const stream = openEventStream({
      url,
      types: ["inbox"],
      onEvent: (e) => {
        const items = (e.payload as { items?: InboxEntry[] } | null)?.items;
        if (items) set(() => ({ state: "ok", items }));
      },
      onReady: () => set((b) => ({ ...b, state: "ok" })),
      onDown: () => set((b) => ({ ...b, state: "down" })),
    });
    return () => stream.close();
  }, [url, qc]);
  return null;
}
